/**
 * Navigation Difficulty List Component
 *
 * Single Responsibility: Manages and displays a list of navigation difficulty items
 * Follows SOLID principles:
 * - Single Responsibility: Only responsible for rendering a list of navigation difficulty items
 * - Open/Closed: Open for extension via props, closed for modification
 * - Dependency Inversion: Depends on NavigationDifficultyItem component interface, not concrete implementation
 */

import React from 'react';
import { spacing } from '@/design-system/utils';
import { NavigationDifficultyItem } from './NavigationDifficultyItem';
import {
  NavigationDifficultyListProps,
  NavigationDifficultyItemData,
} from './types';

/**
 * Navigation Difficulty List Component
 * Renders a list of navigation difficulty items with consistent spacing
 * Supports both string[] (legacy) and NavigationDifficultyItemData[] (structured) formats
 */
export const NavigationDifficultyList: React.FC<
  NavigationDifficultyListProps
> = ({ items, className }) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
      }}
      className={className}
    >
      {items.map((item, index) => {
        // Handle both string format and structured format
        const itemData =
          typeof item === 'string'
            ? { text: item, id: `item-${index}` }
            : (item as NavigationDifficultyItemData);

        return (
          <NavigationDifficultyItem
            key={itemData.id || `item-${index}`}
            item={itemData}
          />
        );
      })}
    </div>
  );
};
